import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = ["src", "alt"]
const _hoisted_2 = ["src", "alt"]

import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { centralizedImageSecurity } from '@/services/centralizedImageSecurity';
import { isLikelyVideoUrl } from '@/utils/validation';

interface Props {
  imageUrl: string;
  wallpaperId: string;
  isPreview?: boolean;
  alt?: string;
  containerClass?: string;
  imageClass?: string;
  aspectClass?: string;
  clickable?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SecureWallpaperImage',
  props: {
    imageUrl: {},
    wallpaperId: {},
    isPreview: { type: Boolean, default: true },
    alt: { default: 'Wallpaper preview' },
    containerClass: { default: 'relative' },
    imageClass: { default: 'w-full h-full object-cover' },
    aspectClass: { default: 'w-full h-full' },
    clickable: { type: Boolean, default: false }
  },
  emits: ["click", "load", "error"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Refs
const containerRef = ref<HTMLElement>();
const imageRef = ref<HTMLImageElement>();
const videoRef = ref<HTMLVideoElement>();
const secureImageUrl = ref<string>('');
const loading = ref(true);
const error = ref(false);
const blobUrls = ref<string[]>([]);
const isVideoUrl = ref(false);

// Computed
const cursorClass = computed(() => {
  return props.clickable ? 'cursor-pointer' : 'cursor-default';
});

// Methods
const loadSecureImage = async () => {
  if (!props.imageUrl || !props.wallpaperId) {
    error.value = true;
    loading.value = false;
    return;
  }

  try {
    loading.value = true;
    error.value = false;
    isVideoUrl.value = isLikelyVideoUrl(props.imageUrl);

    const result = await centralizedImageSecurity.getSecureImageUrl({
      imageUrl: props.imageUrl,
      wallpaperId: props.wallpaperId,
      isPreview: true
    });

    secureImageUrl.value = result.secureUrl;
    
    // Track blob URLs for cleanup
    if (result.secureUrl.startsWith('blob:')) {
      blobUrls.value.push(result.secureUrl);
    }

  } catch (err) {
    console.error('[SecureWallpaperImage] Failed to load secure image:', err);
    error.value = true;
    emit('error', err instanceof Error ? err : new Error('Failed to load image'));
  } finally {
    loading.value = false;
  }
};

const handleMediaLoad = () => {
  loading.value = false;
  error.value = false;
  
  // Apply additional security protections to the image element
  if (imageRef.value) {
    centralizedImageSecurity.protectImageElement(imageRef.value);
  } else if (videoRef.value) {
    centralizedImageSecurity.protectVideoElement(videoRef.value);
  }
  
  emit('load');
};

const handleMediaError = () => {
  loading.value = false;
  error.value = true;
  emit('error', new Error('Image failed to load'));
};

const handleClick = () => {
  if (props.clickable) {
    emit('click');
  }
};

const cleanupBlobUrls = () => {
  blobUrls.value.forEach(url => {
    try {
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn('[SecureWallpaperImage] Failed to revoke blob URL:', err);
    }
  });
  blobUrls.value = [];
};

// Watchers
watch([() => props.imageUrl, () => props.wallpaperId], () => {
  cleanupBlobUrls();
  loadSecureImage();
}, { immediate: false });

// Lifecycle
onMounted(() => {
  loadSecureImage();
  
  // Add global keyboard event prevention
  const handleKeyDown = (e: KeyboardEvent) => {
    // Prevent common save shortcuts when focused on our component
    if (containerRef.value?.contains(document.activeElement)) {
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (e.key === 'F12') {
        e.preventDefault();
        e.stopPropagation();
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };
  
  document.addEventListener('keydown', handleKeyDown);
  
  // Cleanup function
  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyDown);
    cleanupBlobUrls();
  });
});

onUnmounted(() => {
  cleanupBlobUrls();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "containerRef",
    ref: containerRef,
    class: _normalizeClass(["secure-wallpaper-image", _ctx.containerClass]),
    onContextmenu: _cache[7] || (_cache[7] = _withModifiers(() => {}, ["prevent"])),
    onDragstart: _cache[8] || (_cache[8] = _withModifiers(() => {}, ["prevent"])),
    onSelectstart: _cache[9] || (_cache[9] = _withModifiers(() => {}, ["prevent"]))
  }, [
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["flex items-center justify-center bg-gray-100 dark:bg-gray-800 animate-pulse", _ctx.aspectClass])
        }, _cache[10] || (_cache[10] = [
          _createElementVNode("div", { class: "animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500" }, null, -1)
        ]), 2))
      : (error.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 1,
            class: _normalizeClass(["flex items-center justify-center bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400", _ctx.aspectClass])
          }, _cache[11] || (_cache[11] = [
            _createElementVNode("div", { class: "text-center p-4" }, [
              _createElementVNode("svg", {
                class: "w-8 h-8 mx-auto mb-2",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  "stroke-width": "2",
                  d: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"
                })
              ]),
              _createElementVNode("p", { class: "text-sm" }, "Failed to load")
            ], -1)
          ]), 2))
        : (secureImageUrl.value && isVideoUrl.value)
          ? (_openBlock(), _createElementBlock("video", {
              key: 2,
              ref_key: "videoRef",
              ref: videoRef,
              src: secureImageUrl.value,
              alt: _ctx.alt,
              class: _normalizeClass([[_ctx.imageClass, cursorClass.value], "secure-image"]),
              onLoadeddata: handleMediaLoad,
              onError: handleMediaError,
              onClick: handleClick,
              onContextmenu: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["prevent","stop"])),
              onDragstart: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent","stop"])),
              onSelectstart: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["prevent","stop"])),
              draggable: "false",
              oncontextmenu: "return false",
              onselectstart: "return false",
              ondragstart: "return false",
              autoplay: "",
              controlsList: "nodownload noplaybackrate noremoteplayback",
              disablePictureInPicture: "",
              disableRemotePlayback: "",
              muted: "",
              loop: "",
              playsinline: ""
            }, null, 42, _hoisted_1))
          : (secureImageUrl.value)
            ? (_openBlock(), _createElementBlock("img", {
                key: 3,
                ref_key: "imageRef",
                ref: imageRef,
                src: secureImageUrl.value,
                alt: _ctx.alt,
                class: _normalizeClass([[_ctx.imageClass, cursorClass.value], "secure-image"]),
                onLoad: handleMediaLoad,
                onError: handleMediaError,
                onClick: handleClick,
                onContextmenu: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["prevent","stop"])),
                onDragstart: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["prevent","stop"])),
                onSelectstart: _cache[5] || (_cache[5] = _withModifiers(() => {}, ["prevent","stop"])),
                draggable: "false",
                oncontextmenu: "return false",
                onselectstart: "return false",
                ondragstart: "return false"
              }, null, 42, _hoisted_2))
            : _createCommentVNode("", true),
    (secureImageUrl.value && !loading.value && !error.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 4,
          class: "absolute inset-0 pointer-events-none select-none",
          style: {"z-index":"1"},
          onContextmenu: _cache[6] || (_cache[6] = _withModifiers(() => {}, ["prevent","stop"]))
        }, null, 32))
      : _createCommentVNode("", true)
  ], 34))
}
}

})